// Lightweight "Resume where you left off" persistence for the Daily
// Practices library. Position is stored client-side only — there's no
// PII or cross-device sync need for this and localStorage round-trips
// are sub-millisecond so we keep the audio context simple.
//
// Storage shape (single key, single JSON blob):
//   { [practiceId]: { position: <sec>, duration: <sec>, updatedAt: <iso> } }
//
// A practice qualifies for "resume" if its saved position is more than
// MIN_SECONDS into the track AND more than MIN_REMAINING from the end.
// On natural completion (`ended` event) or manual restart we clear the
// entry so the next session starts at 0:00.

const KEY = "bc:practice-progress:v1";
const MIN_SECONDS = 10;
const MIN_REMAINING = 15;

const safeParse = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const safeWrite = (obj) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(obj));
  } catch {
    // Quota or private-mode — silently no-op. Resume is a nice-to-have.
  }
};

// Public API ------------------------------------------------------------

export const getProgress = (practiceId) => {
  if (!practiceId) return null;
  const all = safeParse();
  const entry = all[practiceId];
  if (!entry) return null;
  const { position, duration } = entry;
  if (!Number.isFinite(position) || !Number.isFinite(duration)) return null;
  if (position < MIN_SECONDS) return null;
  if (duration > 0 && position > duration - MIN_REMAINING) return null;
  return entry;
};

export const setProgress = (practiceId, position, duration) => {
  if (!practiceId) return;
  if (!Number.isFinite(position) || !Number.isFinite(duration)) return;
  if (position < MIN_SECONDS) return;
  if (duration > 0 && position > duration - MIN_REMAINING) {
    // Treat near-end as finished — clear so the card resets to a fresh
    // play next session.
    clearProgress(practiceId);
    return;
  }
  const all = safeParse();
  all[practiceId] = {
    position,
    duration,
    updatedAt: new Date().toISOString(),
  };
  safeWrite(all);
};

export const clearProgress = (practiceId) => {
  if (!practiceId) return;
  const all = safeParse();
  if (practiceId in all) {
    delete all[practiceId];
    safeWrite(all);
  }
};

// Format a number of seconds as `m:ss` for UI labels.
export const formatProgressTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};
